<template>
  <div class="page-container">
    <div class="page-header">
      <h2>租金月度结算</h2>
    </div>

    <div class="filter-bar">
      <el-select v-model="shopId" placeholder="选择店铺" size="small" style="width: 200px;" @change="handleShopChange">
        <el-option v-for="shop in shopList" :key="shop.id" :label="shop.name" :value="shop.id" />
      </el-select>

      <el-select v-model="year" placeholder="选择年份" size="small" style="width: 120px; margin-left: 10px;">
        <el-option v-for="y in years" :key="y" :label="y + '年'" :value="y" />
      </el-select>

      <el-button type="primary" size="small" style="margin-left: 10px;" @click="loadSettlement">查询</el-button>
      <el-button type="default" size="small" style="margin-left: 10px;" @click="handleReset">重置</el-button>
      <el-button type="success" size="small" style="margin-left: 10px;" @click="showGenerateDialog">生成结算单</el-button>
    </div>

    <div class="summary-cards" v-if="settlementList.length > 0">
      <el-card class="summary-card">
        <div class="summary-title">结算单数</div>
        <div class="summary-value highlight">{{ settlementList.length }}</div>
      </el-card>
      <el-card class="summary-card">
        <div class="summary-title">订单总数</div>
        <div class="summary-value highlight">{{ totalOrders }}</div>
      </el-card>
      <el-card class="summary-card">
        <div class="summary-title">租金总计</div>
        <div class="summary-value highlight">¥{{ totalRent.toFixed(2) }}</div>
      </el-card>
      <el-card class="summary-card">
        <div class="summary-title">押金总计</div>
        <div class="summary-value">¥{{ totalDeposit.toFixed(2) }}</div>
      </el-card>
    </div>

    <el-table :data="settlementList" v-loading="loading" border stripe>
      <el-table-column prop="settlementMonth" label="结算月份" width="120" align="center">
        <template slot-scope="scope">
          <el-tag type="primary">{{ formatMonth(scope.row.settlementMonth) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="orderCount" label="订单数" width="100" align="center" />
      <el-table-column label="租金总额" width="140" align="center">
        <template slot-scope="scope">
          <span class="amount-value">¥{{ scope.row.totalRent || 0 }}</span>
        </template>
      </el-table-column>
      <el-table-column label="押金总额" width="140" align="center">
        <template slot-scope="scope">
          <span class="amount-value">¥{{ scope.row.totalDeposit || 0 }}</span>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100" align="center">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === 1 ? 'success' : 'warning'" size="small">
            {{ scope.row.status === 1 ? '已确认' : '待确认' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createdAt" label="生成时间" width="160" align="center">
        <template slot-scope="scope">
          {{ formatTime(scope.row.createdAt) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="220" align="center">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="viewOrders(scope.row)">查看订单</el-button>
          <el-button
            v-if="scope.row.status === 0"
            type="text"
            size="small"
            @click="handleConfirm(scope.row)"
          >确认</el-button>
          <el-button
            v-if="scope.row.status === 0"
            type="text"
            size="small"
            style="color: #F56C6C;"
            @click="handleDelete(scope.row)"
          >删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div v-if="settlementList.length === 0 && !loading" class="empty-state">
      <el-empty description="暂无结算数据，请点击"生成结算单"按钮创建" />
    </div>

    <!-- 生成结算单弹窗 -->
    <el-dialog title="生成月度结算单" :visible.sync="showDialog" width="400px">
      <el-form :model="generateForm" label-width="80px">
        <el-form-item label="店铺">
          <el-select v-model="generateForm.shopId" placeholder="选择店铺" style="width: 100%;">
            <el-option v-for="shop in shopList" :key="shop.id" :label="shop.name" :value="shop.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="年份">
          <el-select v-model="generateForm.year" placeholder="选择年份" style="width: 100%;">
            <el-option v-for="y in years" :key="y" :label="y + '年'" :value="y" />
          </el-select>
        </el-form-item>
        <el-form-item label="月份">
          <el-select v-model="generateForm.month" placeholder="选择月份" style="width: 100%;">
            <el-option v-for="m in months" :key="m" :label="m + '月'" :value="m" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="showDialog = false">取消</el-button>
        <el-button type="primary" :loading="generating" @click="handleGenerate">生成</el-button>
      </div>
    </el-dialog>

    <!-- 订单详情弹窗 -->
    <el-dialog :title="'结算单详情 - ' + currentMonthLabel" :visible.sync="showDetailDialog" width="90%" top="5vh">
      <el-table :data="detailOrders" v-loading="detailLoading" border stripe max-height="500">
        <el-table-column prop="orderNo" label="订单号" min-width="160" />
        <el-table-column prop="goodsName" label="商品" min-width="120" />
        <el-table-column prop="tenantName" label="租客" min-width="100" />
        <el-table-column prop="rentDays" label="租期(天)" width="90" align="center" />
        <el-table-column label="租金" width="120" align="center">
          <template slot-scope="scope">
            ¥{{ formatPrice(scope.row.orderPrice) }}
          </template>
        </el-table-column>
        <el-table-column label="押金" width="120" align="center">
          <template slot-scope="scope">
            ¥{{ formatPrice(scope.row.depositPrice) }}
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="getStatusType(scope.row.status)" size="small">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="下单时间" width="160" />
      </el-table>
      <div slot="footer">
        <el-button @click="showDetailDialog = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getShopList } from '@/api/shop'
import { getSettlementList, generateSettlement, confirmSettlement, deleteSettlement } from '@/api/settlement'
import { getMonthlySettlementOrders, exportBill } from '@/api/order'
import { orderStatusMap, orderStatusTypeMap } from '@common/statusMap.js'

export default {
  name: 'RentSettlement',
  data() {
    const now = new Date()
    const year = now.getFullYear()
    const years = []
    for (let i = year - 5; i <= year; i++) {
      years.push(i)
    }
    const months = []
    for (let i = 1; i <= 12; i++) {
      months.push(i)
    }

    return {
      shopList: [],
      shopId: '',
      settlementList: [],
      detailOrders: [],
      loading: false,
      detailLoading: false,
      generating: false,
      year: year,
      years,
      months,
      currentMonth: '',
      currentMonthLabel: '',
      currentYear: year,
      currentMonthNum: 0,
      showDetailDialog: false,
      showDialog: false,
      generateForm: {
        shopId: '',
        year: year,
        month: now.getMonth() + 1
      }
    }
  },
  computed: {
    totalOrders() {
      return this.settlementList.reduce((sum, item) => sum + (item.orderCount || 0), 0)
    },
    totalRent() {
      return this.settlementList.reduce((sum, item) => sum + (item.totalRent || 0), 0)
    },
    totalDeposit() {
      return this.settlementList.reduce((sum, item) => sum + (item.totalDeposit || 0), 0)
    }
  },
  mounted() {
    this.loadShops()
  },
  methods: {
    async loadShops() {
      try {
        const res = await getShopList({ page: 1, pageSize: 100 })
        this.shopList = res.data?.list || res.data?.records || []
        if (this.shopList.length > 0) {
          this.shopId = this.shopList[0].id
          this.generateForm.shopId = this.shopId
          this.loadSettlement()
        }
      } catch (e) {
        this.$message.error('加载店铺列表失败')
      }
    },
    handleShopChange() {
      this.generateForm.shopId = this.shopId
      this.loadSettlement()
    },
    async loadSettlement() {
      if (!this.shopId) return
      this.loading = true
      try {
        const res = await getSettlementList({
          shopId: this.shopId,
          year: this.year
        })
        this.settlementList = res.data || []
      } catch (e) {
        this.$message.error('加载结算单失败')
      } finally {
        this.loading = false
      }
    },
    handleReset() {
      const now = new Date()
      this.year = now.getFullYear()
      this.loadSettlement()
    },
    showGenerateDialog() {
      this.generateForm.shopId = this.shopId
      this.generateForm.year = this.year
      this.generateForm.month = new Date().getMonth() + 1
      this.showDialog = true
    },
    async handleGenerate() {
      if (!this.generateForm.shopId) {
        this.$message.warning('请选择店铺')
        return
      }
      if (!this.generateForm.year) {
        this.$message.warning('请选择年份')
        return
      }
      if (!this.generateForm.month) {
        this.$message.warning('请选择月份')
        return
      }

      this.generating = true
      try {
        await generateSettlement(this.generateForm)
        this.$message.success('生成成功')
        this.showDialog = false
        this.loadSettlement()
      } catch (e) {
        this.$message.error(e.message || '生成失败')
      } finally {
        this.generating = false
      }
    },
    async handleConfirm(row) {
      try {
        const res = await this.$confirm('确认该结算单？确认后将无法修改。', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        if (res === 'confirm') {
          await confirmSettlement(row.id)
          this.$message.success('已确认')
          this.loadSettlement()
        }
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error(e.message || '操作失败')
        }
      }
    },
    async handleDelete(row) {
      try {
        const res = await this.$confirm('确定删除该结算单？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        if (res === 'confirm') {
          await deleteSettlement(row.id)
          this.$message.success('已删除')
          this.loadSettlement()
        }
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error(e.message || '删除失败')
        }
      }
    },
    formatMonth(monthStr) {
      if (!monthStr) return '-'
      return monthStr
    },
    formatTime(time) {
      if (!time) return '-'
      return time.replace('T', ' ').substring(0, 19)
    },
    async viewOrders(row) {
      this.currentMonth = row.settlementMonth
      const parts = row.settlementMonth.split('-')
      this.currentYear = parseInt(parts[0])
      this.currentMonthNum = parseInt(parts[1])
      this.currentMonthLabel = `${this.currentYear}年${this.currentMonthNum}月结算单`
      this.showDetailDialog = true
      this.detailLoading = true

      try {
        const res = await getMonthlySettlementOrders({
          shopId: this.shopId,
          year: this.currentYear,
          month: this.currentMonthNum
        })
        this.detailOrders = res.data || []
      } catch (e) {
        this.$message.error('加载订单详情失败')
      } finally {
        this.detailLoading = false
      }
    },
    formatPrice(price) {
      if (!price) return '0.00'
      const num = Number(price)
      if (isNaN(num)) return '0.00'
      return num.toFixed(2)
    },
    getStatusType(status) {
      return orderStatusTypeMap[status] || 'info'
    },
    getStatusText(status) {
      return orderStatusMap[status] || status
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 20px;
}
.page-header {
  margin-bottom: 20px;
}
.page-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}
.filter-bar {
  margin-bottom: 20px;
  padding: 15px 20px;
  background: #f8f9fa;
  border-radius: 8px;
}
.summary-cards {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}
.summary-card {
  flex: 1;
  text-align: center;
}
.summary-title {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}
.summary-value {
  font-size: 24px;
  font-weight: bold;
  color: #333;
}
.summary-value.highlight {
  color: #409EFF;
}
.amount-value {
  color: #F56C6C;
  font-weight: 500;
}
.empty-state {
  padding: 60px 0;
}
</style>
